/**
 * Période d'observation — module PUR.
 *
 * Une seule période gouverne toutes les pages : deux graphiques du même écran
 * qui couvrent des fenêtres différentes racontent deux histoires (cf.
 * `DATA-MODEL.md` §3). Le pas de temps (`bucket`) est **déduit** de la durée et
 * non choisi par l'utilisateur : une plage de 30 jours en pas horaire produit
 * 720 points illisibles, une plage de 24 h en pas journalier en produit deux.
 */

import type { Bucket, RangeParams } from './api';

export type RangeKey = '24h' | '7d' | '30d' | 'custom';

export interface Range extends RangeParams {
  key: RangeKey;
  from: string;
  to: string;
  bucket: Bucket;
  /** Libellé prêt à afficher, en français. */
  label: string;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Au-delà d'environ trois jours, le pas horaire cesse d'être lisible. */
function bucketFor(spanMs: number): Bucket {
  return spanMs <= 3 * DAY_MS ? 'hour' : 'day';
}

export function presetRange(key: Exclude<RangeKey, 'custom'>, now = new Date()): Range {
  const spans: Record<Exclude<RangeKey, 'custom'>, { ms: number; label: string }> = {
    '24h': { ms: DAY_MS, label: 'dernières 24 h' },
    '7d': { ms: 7 * DAY_MS, label: '7 derniers jours' },
    '30d': { ms: 30 * DAY_MS, label: '30 derniers jours' },
  };
  const span = spans[key];
  const to = now;
  const from = new Date(now.getTime() - span.ms);
  return {
    key,
    from: from.toISOString(),
    to: to.toISOString(),
    bucket: bucketFor(span.ms),
    label: span.label,
  };
}

/**
 * Période personnalisée à partir de deux dates `YYYY-MM-DD`.
 * La borne haute est portée à la fin du jour choisi : un opérateur qui saisit
 * « du 1er au 3 » attend que le 3 soit inclus.
 */
export function customRange(fromDay: string, toDay: string): Range | null {
  const from = new Date(`${fromDay}T00:00:00.000Z`);
  const to = new Date(`${toDay}T23:59:59.999Z`);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return null;
  if (from.getTime() > to.getTime()) return null;
  const span = to.getTime() - from.getTime();
  // Garde-fou aligné sur le serveur : au-delà de 400 jours, l'API répond 400.
  if (span > 400 * DAY_MS) return null;
  return {
    key: 'custom',
    from: from.toISOString(),
    to: to.toISOString(),
    bucket: bucketFor(span),
    label: `du ${fromDay} au ${toDay}`,
  };
}

/** Clé de dépendance stable pour les effets React. */
export function rangeKey(range: Range): string {
  return `${range.from}|${range.to}|${range.bucket}`;
}

/**
 * Période précédente de MÊME DURÉE, collée juste avant celle-ci.
 *
 * Sert aux écarts affichés sur les tuiles (« +22 % vs période précédente »).
 * Deux règles s'imposent d'elles-mêmes :
 *  · même durée, sinon on compare 24 h à 7 j et l'écart ne veut rien dire ;
 *  · même pas de temps (`bucket`), pour que les deux appels agrègent pareil.
 *
 * On ne fabrique JAMAIS un écart autrement : comparer un chiffre de période à
 * un chiffre cumulé depuis l'ouverture du service produirait un pourcentage
 * juste dans son calcul et faux dans ce qu'il raconte.
 */
export function previousRange(range: Range): Range {
  const from = new Date(range.from).getTime();
  const to = new Date(range.to).getTime();
  const span = to - from;
  return {
    ...range,
    key: 'custom',
    from: new Date(from - span).toISOString(),
    to: new Date(from).toISOString(),
    label: `période précédente (${range.label})`,
  };
}
